import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import AssessmentService from "src/services/assessment/assessmentService";
import { CreateTraPayload } from "src/store/createAssessmentSlice";
import { LMEMetadataDetail } from "src/services/assessment/types/lmeMetadataDetail";
import {
  CREATE_ASSESSMENT_COMMENT_CHARACTER_LIMIT,
  LME_ACTIVITY_TYPES,
  LME_UNIT_SYSTEM,
  LME_FREQUENCY_UNITS,
  LmeActivityType,
  LmeFrequencyUnit,
  LmeUnitSystem,
  isLiftOrLower,
  isPushOrPull,
} from "src/constants/constants";

export type createLmeAssessmentState = {
  assessmentId: string;
  evaluationId: string;
  lmeActivityType: LmeActivityType | "";
  // Locked (dropdown disabled) on follow-up, since backend enforces
  // lmeActivityType stays identical to the initial evaluation's value.
  isLmeActivityTypeLocked: boolean;
  unitSystem: LmeUnitSystem;
  frequency: string;
  frequencyUnit: LmeFrequencyUnit;

  // CARRY / shared with PUSH_PULL
  horizontalDistance: string;
  verticalHandHeight: string;
  // CARRY / shared with LIFT_LOWER
  loadWeight: string;

  // LIFT_LOWER
  handHeightStart: string;
  handHeightEnd: string;
  horizontalLocationStart: string;
  horizontalLocationEnd: string;

  // PUSH_PULL. The user enters one average force per phase. Named
  // *Input to avoid colliding with averageInitialForce /
  // averageSustainedForce below, which are numeric calculation *results*.
  initialForceInput: string;
  sustainedForceInput: string;

  comment: string;
  isCommentsValid: boolean;

  // Computed results (frontend-calculated, see lmeCalculationService)
  malAt75Pct: number;
  capablePopulationPct: number;
  sustainedMalAt75Pct: number;
  sustainedCapablePopulationPct: number;
  averageInitialForce: number;
  averageSustainedForce: number;
  hasCalculatedInitialScore: boolean;

  isLoading: boolean;
  isFulfilled: boolean;
  isError: boolean;
};

const initialState: createLmeAssessmentState = {
  assessmentId: "",
  evaluationId: "",
  lmeActivityType: "",
  isLmeActivityTypeLocked: false,
  unitSystem: LME_UNIT_SYSTEM.METRIC,
  frequency: "",
  frequencyUnit: LME_FREQUENCY_UNITS.ACTIONS_PER_MIN,

  horizontalDistance: "",
  verticalHandHeight: "",
  loadWeight: "",

  handHeightStart: "",
  handHeightEnd: "",
  horizontalLocationStart: "",
  horizontalLocationEnd: "",

  initialForceInput: "",
  sustainedForceInput: "",

  comment: "",
  isCommentsValid: true,

  malAt75Pct: 0,
  capablePopulationPct: 0,
  sustainedMalAt75Pct: 0,
  sustainedCapablePopulationPct: 0,
  averageInitialForce: 0,
  averageSustainedForce: 0,
  hasCalculatedInitialScore: false,

  isLoading: false,
  isFulfilled: false,
  isError: false,
};

// Every activity-specific input/result field, reset whenever the user
// switches Activity Type. Several fields are shared across multiple forms by
// name (horizontalDistance/verticalHandHeight between Carry and Push/Pull;
// loadWeight between Carry and Lift/Lower; frequency/frequencyUnit across all
// three) - without this reset, values entered under one activity type would
// silently reappear in another type's form. Deliberately excludes
// assessmentId, evaluationId, lmeActivityType, isLmeActivityTypeLocked
// (identity/lock state, not form input) and comment/isCommentsValid (not
// activity-specific, no reason to lose a note the user already wrote).
const getFreshInputFields = () => ({
  unitSystem: initialState.unitSystem,
  frequency: initialState.frequency,
  frequencyUnit: initialState.frequencyUnit,

  horizontalDistance: initialState.horizontalDistance,
  verticalHandHeight: initialState.verticalHandHeight,
  loadWeight: initialState.loadWeight,

  handHeightStart: initialState.handHeightStart,
  handHeightEnd: initialState.handHeightEnd,
  horizontalLocationStart: initialState.horizontalLocationStart,
  horizontalLocationEnd: initialState.horizontalLocationEnd,

  initialForceInput: initialState.initialForceInput,
  sustainedForceInput: initialState.sustainedForceInput,

  malAt75Pct: initialState.malAt75Pct,
  capablePopulationPct: initialState.capablePopulationPct,
  sustainedMalAt75Pct: initialState.sustainedMalAt75Pct,
  sustainedCapablePopulationPct: initialState.sustainedCapablePopulationPct,
  averageInitialForce: initialState.averageInitialForce,
  averageSustainedForce: initialState.averageSustainedForce,
  hasCalculatedInitialScore: initialState.hasCalculatedInitialScore,
});

const buildLmeMetadataPayload = (
  state: createLmeAssessmentState,
): LMEMetadataDetail => {
  const base: LMEMetadataDetail = {
    unitSystem: state.unitSystem,
    frequency: Number(state.frequency),
    frequencyUnit: state.frequencyUnit,
    malAt75Pct: state.malAt75Pct,
    capablePopulationPct: state.capablePopulationPct,
    comment: state.comment || undefined,
  };

  if (state.lmeActivityType === LME_ACTIVITY_TYPES.CARRY) {
    return {
      ...base,
      horizontalDistance: Number(state.horizontalDistance),
      verticalHandHeight: Number(state.verticalHandHeight),
      loadWeight: Number(state.loadWeight),
    };
  }

  if (isLiftOrLower(state.lmeActivityType)) {
    return {
      ...base,
      handHeightStart: Number(state.handHeightStart),
      handHeightEnd: Number(state.handHeightEnd),
      horizontalLocationStart: Number(state.horizontalLocationStart),
      horizontalLocationEnd: Number(state.horizontalLocationEnd),
      loadWeight: Number(state.loadWeight),
    };
  }

  if (isPushOrPull(state.lmeActivityType)) {
    return {
      ...base,
      horizontalDistance: Number(state.horizontalDistance),
      verticalHandHeight: Number(state.verticalHandHeight),
      // The UI now collects a single average force per phase, but
      // SaveLMEMetadataActivity.validatePushPull in ErgonaService rejects
      // PUSH/PULL unless initialForceValues and sustainedForceValues each
      // contain EXACTLY 3 entries (it checks `size() != 3`, so one value
      // fails as hard as none). The arrays are write-only on the backend -
      // they are copied straight to the record in toLMEAssessmentMetadata,
      // no calculation reads them, and nothing cross-checks them against
      // the averages - so repeating the entered average three times
      // satisfies the contract without distorting anything: the mean of
      // three identical values is that value.

      initialForceValues: [
        state.averageInitialForce,
        state.averageInitialForce,
        state.averageInitialForce,
      ],
      sustainedForceValues: [
        state.averageSustainedForce,
        state.averageSustainedForce,
        state.averageSustainedForce,
      ],
      averageInitialForce: state.averageInitialForce,
      averageSustainedForce: state.averageSustainedForce,
      sustainedMalAt75Pct: state.sustainedMalAt75Pct,
      sustainedCapablePopulationPct: state.sustainedCapablePopulationPct,
    };
  }

  return base;
};

export const saveLMEMetadata = createAsyncThunk(
  "createLmeAssessment/saveLMEMetadata",
  async (payload: { clientId: string; evaluationId: string }, { getState }) => {
    const state = (await (getState() as any)
      .createLmeAssessment) as createLmeAssessmentState;
    const { data } = await AssessmentService.saveLMEMetadata({
      clientId: payload.clientId,
      evaluationId: payload.evaluationId,
      lmeActivityType: state.lmeActivityType,
      lmeMetadata: buildLmeMetadataPayload(state),
    });
    return data;
  },
);

// For new lme assessments one dispatch from the component chains createAssessment -> saveLMEMetadata.
// If an evaluationId is already supplied (follow-up entry against an existing assessment), skip
// createAssessment and save metadata directly against it.
export const createAndSaveLMEAssessment = createAsyncThunk(
  "createLmeAssessment/createAndSaveLMEAssessment",
  async (
    payload: {
      clientId: string;
      traCreatePayload?: CreateTraPayload;
      evaluationId?: string;
    },
    { getState },
  ) => {
    const state = (await (getState() as any)
      .createLmeAssessment) as createLmeAssessmentState;

    const evaluationId: string = payload.evaluationId
      ? payload.evaluationId
      : (await AssessmentService.createAssessment(payload.traCreatePayload!))
          .data.initialEvaluationId;

    const { data } = await AssessmentService.saveLMEMetadata({
      clientId: payload.clientId,
      evaluationId,
      lmeActivityType: state.lmeActivityType,
      lmeMetadata: buildLmeMetadataPayload(state),
    });
    return data;
  },
);

const { reducer, actions } = createSlice({
  name: "createLmeAssessment",
  initialState,
  reducers: {
    setLmeAssessmentId: (state, action) => {
      state.assessmentId = action.payload;
    },
    setLmeEvaluationId: (state, action) => {
      state.evaluationId = action.payload;
    },
    setLmeActivityType: (state, action) => {
      if (state.isLmeActivityTypeLocked) return;
      // Clear all activity-specific inputs/results on an actual type switch,
      // so values entered under the previous activity type don't leak into
      // (or reappear later in) the newly-selected type's form.
      if (state.lmeActivityType !== action.payload) {
        Object.assign(state, getFreshInputFields());
      }
      state.lmeActivityType = action.payload;
    },
    // Called when switching away from LME back to REBA/RULA on step 1, so a
    // stale selection doesn't linger if the user comes back to LME later.
    clearLmeActivityType: (state) => {
      if (state.isLmeActivityTypeLocked) return;
      Object.assign(state, getFreshInputFields());
      state.lmeActivityType = "";
    },
    setLmeActivityTypeLocked: (state, action) => {
      state.isLmeActivityTypeLocked = action.payload;
    },
    // Sets lmeActivityType and locks it in one atomic dispatch, bypassing the
    // isLmeActivityTypeLocked guard on setLmeActivityType above. Needed
    // because Redux state can carry over between navigations within the SPA -
    // if isLmeActivityTypeLocked were already true from a previous session
    // (e.g. a prior follow-up), a plain setLmeActivityType dispatch would
    // silently no-op and leave the dropdown locked on a stale value. Used
    // when pre-filling the dropdown for an existing LME assessment
    // (edit/follow-up entry into Step 1).
    setLockedLmeActivityType: (state, action) => {
      state.lmeActivityType = action.payload;
      state.isLmeActivityTypeLocked = true;
    },
    setLmeUnitSystem: (state, action) => {
      state.unitSystem = action.payload;
    },
    setLmeFrequency: (state, action) => {
      state.frequency = action.payload;
      state.hasCalculatedInitialScore = false;
    },
    setLmeFrequencyUnit: (state, action) => {
      state.frequencyUnit = action.payload;
      state.hasCalculatedInitialScore = false;
    },
    setLmeHorizontalDistance: (state, action) => {
      state.horizontalDistance = action.payload;
      state.hasCalculatedInitialScore = false;
    },
    setLmeVerticalHandHeight: (state, action) => {
      state.verticalHandHeight = action.payload;
      state.hasCalculatedInitialScore = false;
    },
    setLmeLoadWeight: (state, action) => {
      state.loadWeight = action.payload;
      state.hasCalculatedInitialScore = false;
    },
    setLmeHandHeightStart: (state, action) => {
      state.handHeightStart = action.payload;
      state.hasCalculatedInitialScore = false;
    },
    setLmeHandHeightEnd: (state, action) => {
      state.handHeightEnd = action.payload;
      state.hasCalculatedInitialScore = false;
    },
    setLmeHorizontalLocationStart: (state, action) => {
      state.horizontalLocationStart = action.payload;
      state.hasCalculatedInitialScore = false;
    },
    setLmeHorizontalLocationEnd: (state, action) => {
      state.horizontalLocationEnd = action.payload;
      state.hasCalculatedInitialScore = false;
    },
    setLmeInitialForceInput: (state, action) => {
      state.initialForceInput = action.payload;
      state.hasCalculatedInitialScore = false;
    },
    setLmeSustainedForceInput: (state, action) => {
      state.sustainedForceInput = action.payload;
      state.hasCalculatedInitialScore = false;
    },
    setLmeComment: (state, action) => {
      state.comment = action.payload;
      state.isCommentsValid =
        state.comment.length <= CREATE_ASSESSMENT_COMMENT_CHARACTER_LIMIT;
    },
    setLmeCalculationResult: (
      state,
      action: {
        payload: {
          malAt75Pct: number;
          capablePopulationPct: number;
          sustainedMalAt75Pct?: number;
          sustainedCapablePopulationPct?: number;
          averageInitialForce?: number;
          averageSustainedForce?: number;
        };
      },
    ) => {
      state.malAt75Pct = action.payload.malAt75Pct;
      state.capablePopulationPct = action.payload.capablePopulationPct;
      state.sustainedMalAt75Pct = action.payload.sustainedMalAt75Pct ?? 0;
      state.sustainedCapablePopulationPct =
        action.payload.sustainedCapablePopulationPct ?? 0;
      state.averageInitialForce = action.payload.averageInitialForce ?? 0;
      state.averageSustainedForce = action.payload.averageSustainedForce ?? 0;
      state.hasCalculatedInitialScore = true;
    },
    resetCreateLmeAssessment: (state) => {
      // Preserve lmeActivityType/isLmeActivityTypeLocked across this reset -
      // dispatched on component unmount, which can happen mid-follow-up-entry
      // (e.g. navigating away and back). Callers that truly want a clean
      // slate (e.g. starting a brand new create flow) should also dispatch
      // setLmeActivityTypeLocked(false) explicitly.
      return {
        ...initialState,
        // preserve lock/activity type when resetting mid-follow-up-entry;
        // callers that truly want a clean slate should also dispatch
        // setLmeActivityTypeLocked(false) explicitly.
        lmeActivityType: state.lmeActivityType,
        isLmeActivityTypeLocked: state.isLmeActivityTypeLocked,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveLMEMetadata.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
    });
    builder.addCase(saveLMEMetadata.fulfilled, (state) => {
      state.isLoading = false;
      state.isFulfilled = true;
    });
    builder.addCase(saveLMEMetadata.rejected, (state) => {
      state.isLoading = false;
      state.isError = true;
    });
    builder.addCase(createAndSaveLMEAssessment.pending, (state) => {
      state.isLoading = true;
      state.isError = false;
    });
    builder.addCase(createAndSaveLMEAssessment.fulfilled, (state) => {
      state.isLoading = false;
      state.isFulfilled = true;
    });
    builder.addCase(createAndSaveLMEAssessment.rejected, (state) => {
      state.isLoading = false;
      state.isError = true;
    });
  },
});

export const {
  setLmeAssessmentId,
  setLmeEvaluationId,
  setLmeActivityType,
  clearLmeActivityType,
  setLmeActivityTypeLocked,
  setLockedLmeActivityType,
  setLmeUnitSystem,
  setLmeFrequency,
  setLmeFrequencyUnit,
  setLmeHorizontalDistance,
  setLmeVerticalHandHeight,
  setLmeLoadWeight,
  setLmeHandHeightStart,
  setLmeHandHeightEnd,
  setLmeHorizontalLocationStart,
  setLmeHorizontalLocationEnd,
  setLmeInitialForceInput,
  setLmeSustainedForceInput,
  setLmeComment,
  setLmeCalculationResult,
  resetCreateLmeAssessment,
} = actions;

export default reducer;
